/**
 * Lógica pura de los canales de impresión (sin React ni Supabase), testeable de forma aislada.
 *
 * Un canal es independiente: el fallo de uno no debe impedir que los otros impriman.
 */

export const PRINT_CHANNELS = ['caja', 'cocina', 'fiscal'] as const;

export type PrintChannel = (typeof PRINT_CHANNELS)[number];

export type ChannelStatus = 'idle' | 'loading' | 'success' | 'error' | 'skipped';

export interface ChannelState {
  status: ChannelStatus;
  message?: string;
  at?: string;
}

export type ChannelStates = Record<PrintChannel, ChannelState>;

/** Estado persistido en `orders.metadata.printing` (solo resultados terminales). */
export type PersistedChannelStatus = 'success' | 'error' | 'skipped';

export interface PersistedChannelState {
  status: PersistedChannelStatus;
  at?: string;
  error?: string;
}

export type PrintingMetadata = Partial<Record<PrintChannel, PersistedChannelState>>;

export interface PrintablePrinter {
  id: string;
  name: string;
  ip: string;
  port: number;
  type?: string | null;
}

/** Impresora asignada por canal en esta máquina (localStorage). `null` = sin asignar. */
export interface ChannelAssignments {
  caja: string | null;
  cocina: string | null;
  fiscal: string | null;
}

export type PartialChannelAssignments = Partial<ChannelAssignments>;

export interface ChannelTargets {
  caja: PrintablePrinter | null;
  cocina: PrintablePrinter | null;
  fiscal: PrintablePrinter | null;
}

export const INITIAL_CHANNEL_STATES: ChannelStates = {
  caja: { status: 'idle' },
  cocina: { status: 'idle' },
  fiscal: { status: 'idle' },
};

export function isPrintChannel(value: unknown): value is PrintChannel {
  return typeof value === 'string' && (PRINT_CHANNELS as readonly string[]).includes(value);
}

function isPersistedChannelState(value: unknown): value is PersistedChannelState {
  if (!value || typeof value !== 'object') return false;
  const status = (value as { status?: unknown }).status;
  return status === 'success' || status === 'error' || status === 'skipped';
}

/**
 * Lee `orders.metadata.printing` de forma segura. Cualquier forma inesperada se ignora:
 * la metadata es `jsonb` libre y no debe romper la pantalla de éxito.
 */
export function parsePrintingMetadata(metadata: unknown): PrintingMetadata {
  if (!metadata || typeof metadata !== 'object') return {};

  const printing = (metadata as { printing?: unknown }).printing;
  if (!printing || typeof printing !== 'object') return {};

  const parsed: PrintingMetadata = {};

  for (const channel of PRINT_CHANNELS) {
    const value = (printing as Record<string, unknown>)[channel];
    if (!isPersistedChannelState(value)) continue;

    parsed[channel] =
      value.status === 'error'
        ? {
            status: value.status,
            at: typeof value.at === 'string' ? value.at : undefined,
            error: typeof value.error === 'string' ? value.error : undefined,
          }
        : { status: value.status, at: typeof value.at === 'string' ? value.at : undefined };
  }

  return parsed;
}

/** Un canal ya resuelto (impreso u omitido a propósito) no se vuelve a disparar. */
export function shouldRunChannel(printing: PrintingMetadata, channel: PrintChannel): boolean {
  const status = printing[channel]?.status;
  return status !== 'success' && status !== 'skipped';
}

export function getPendingChannels(printing: PrintingMetadata): PrintChannel[] {
  return PRINT_CHANNELS.filter(channel => shouldRunChannel(printing, channel));
}

function normalizeName(name: string): string {
  return name.trim().toLowerCase();
}

/** Impresora de cocina por tipo, o por nombre con "cocina". */
export function isKitchenPrinter(printer: PrintablePrinter): boolean {
  return printer.type === 'kitchen' || normalizeName(printer.name).includes('cocina');
}

/**
 * Lee las asignaciones por canal de esta máquina desde `localStorage`.
 * Cualquier forma inesperada se ignora: no debe romper la UI de impresión.
 */
export function parseChannelAssignments(raw: string | null | undefined): PartialChannelAssignments {
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};

    const assignments: PartialChannelAssignments = {};

    for (const channel of PRINT_CHANNELS) {
      const value = (parsed as Record<string, unknown>)[channel];
      if (typeof value === 'string' && value) assignments[channel] = value;
    }

    return assignments;
  } catch {
    return {};
  }
}

export function serializeChannelAssignments(assignments: PartialChannelAssignments): string {
  return JSON.stringify({
    caja: assignments.caja ?? null,
    cocina: assignments.cocina ?? null,
    fiscal: assignments.fiscal ?? null,
  });
}

/**
 * Resuelve a qué impresora va cada canal en esta máquina:
 * - La asignación explícita (por canal) manda; si la impresora ya no existe, cae al fallback.
 * - caja: fallback por detección (probe/subred/tipo) que calcula el provider.
 * - cocina: fallback la primera impresora de cocina (reachable primero).
 * - fiscal: fallback la misma de caja (sin asignación explícita).
 */
export function resolveChannelTargets(
  printers: PrintablePrinter[],
  assignments: PartialChannelAssignments | null | undefined,
  fallbacks: { caja: PrintablePrinter | null; cocina: PrintablePrinter | null }
): ChannelTargets {
  const byId = (id?: string | null) => (id ? (printers.find(printer => printer.id === id) ?? null) : null);

  const caja = byId(assignments?.caja) ?? fallbacks.caja;
  const cocina = byId(assignments?.cocina) ?? fallbacks.cocina;
  const fiscal = byId(assignments?.fiscal) ?? caja;

  return { caja, cocina, fiscal };
}

/** Aplica el resultado de un canal sin mutar el estado anterior. */
export function applyChannelState(states: ChannelStates, channel: PrintChannel, next: ChannelState): ChannelStates {
  return { ...states, [channel]: next };
}

/** `true` si hay algún canal en vuelo (para deshabilitar botones y evitar dobles envíos). */
export function isPrintingChannels(states: ChannelStates): boolean {
  return PRINT_CHANNELS.some(channel => states[channel].status === 'loading');
}

/** Reduce los estados de una tanda a lo persistible en `orders.metadata.printing`. */
export function toPersistedChannels(states: ChannelStates, channels: PrintChannel[]): PrintingMetadata {
  const persisted: PrintingMetadata = {};

  for (const channel of channels) {
    const state = states[channel];

    if (state.status === 'success' || state.status === 'skipped') {
      persisted[channel] = { status: state.status, at: state.at };
    } else if (state.status === 'error') {
      persisted[channel] = { status: 'error', at: state.at, error: state.message };
    }
  }

  return persisted;
}
